import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Trash2, 
  Mail,
  Phone,
  CheckCircle,
  Clock,
  MessageSquare,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/contacts`);
      setMessages(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleMarkRead = async (messageId) => {
    try {
      await axios.patch(`${API_URL}/api/admin/contacts/${messageId}/read`);
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Sigur vrei să ștergi acest mesaj?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/contacts/${messageId}`);
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('ro-RO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Mesaje</h1>
        <p className="text-slate-400 mt-1">
          {messages.filter(m => m.status === 'new').length} mesaje noi din {messages.length} total
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h2 className="font-bold text-white">Inbox</h2>
          </div>
          
          {messages.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Nu ai mesaje</p>
            </div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto divide-y divide-slate-700">
              {messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (message.status === 'new') {
                      handleMarkRead(message.id);
                    }
                  }}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedMessage?.id === message.id 
                      ? 'bg-sky-500/20' 
                      : 'hover:bg-slate-700/50'
                  } ${message.status === 'new' ? 'border-l-4 border-l-amber-500' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className={`font-medium truncate ${message.status === 'new' ? 'text-white' : 'text-slate-300'}`}>
                      {message.name}
                    </p>
                    {message.status === 'new' && (
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex-shrink-0">
                        Nou
                      </span>
                    )}
                  </div>
                  <p className="text-slate-400 text-sm truncate">{message.message}</p>
                  <p className="text-slate-500 text-xs mt-1">{formatDate(message.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2 bg-slate-800 rounded-2xl border border-slate-700">
          {selectedMessage ? (
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-sky-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {selectedMessage.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedMessage.name}</h2>
                    <p className="text-slate-400 text-sm">{formatDate(selectedMessage.created_at)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleDelete(selectedMessage.id)}
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Contact Info */}
              <div className="p-6 border-b border-slate-700 flex flex-wrap gap-4">
                <a 
                  href={`mailto:${selectedMessage.email}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-sky-400 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {selectedMessage.email}
                </a>
                {selectedMessage.phone && (
                  <a 
                    href={`tel:${selectedMessage.phone}`}
                    className="flex items-center gap-2 text-slate-300 hover:text-sky-400 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {selectedMessage.phone}
                  </a>
                )}
              </div>

              {/* Message Content */}
              <div className="p-6 flex-1">
                <p className="text-white whitespace-pre-wrap leading-relaxed">
                  {selectedMessage.message}
                </p>
              </div>

              {/* Actions */}
              <div className="p-6 border-t border-slate-700 flex flex-wrap gap-3">
                <a
                  href={`mailto:${selectedMessage.email}`}
                  className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-xl transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Răspunde pe Email
                </a>
                {selectedMessage.phone && (
                  <a
                    href={`https://wa.me/${selectedMessage.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-8">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Selectează un mesaj pentru a vedea detaliile</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
